import React from "react";
import { Button, Col, Input, Row } from "antd";
import {
  AppstoreOutlined,
  BarcodeOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  ControlOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  FilterOutlined,
  GlobalOutlined,
  HistoryOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import FilterDropdown from "./FilterDropdown";
import SortGroup from "./SortGroup";
import { formatSnakeCase } from "../helpers/utilities";
import type { IField } from "./AdvancedSearchModal";
import "../styles/components/PageToolbar.css";

export interface PageToolbarProps {
  // Search section
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  advancedSearch?: ToolbarAdvancedSearchConfig;

  // Actions section
  actions?: React.ReactNode | React.ReactNode[];
  primaryAction?: React.ReactNode;

  // Filters section
  filters?: React.ReactNode | React.ReactNode[];

  // Pagination / Record Info section
  selectedCount?: number;
  modelName?: string;

  totalCount?: any;
  currentDataCount?: any;
  page?: any;
  pageSize?: any;
  actionsInfo?: any;
}

export interface ToolbarAdvancedSearchConfig {
  onOpen: () => void;
  activeFields?: IField[];
  onFieldRemove?: (field: IField) => void;
}

export interface ToolbarFilterConfig {
  label: string;
  options: Array<{ value: string; label: string; color?: string }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  icon?: React.ReactNode;
  placeholder?: string;
  width?: number;
  loading?: boolean;
  onSearch?: (value: string) => void;
  mode?: "multiple" | "single";
}

export interface ToolbarSortConfig {
  field: string;
  direction: "ASC" | "DESC" | string;
  options: Array<{ value: string; label: string }>;
  onChange: (field: string, direction: "ASC" | "DESC" | any) => void;
}

export const PageToolbar: React.FC<PageToolbarProps> & {
  Actions: React.FC<{ children: React.ReactNode }>;
  Filters: React.FC<{
    configs?: ToolbarFilterConfig[];
    sort?: ToolbarSortConfig;
    onClear?: () => void;
    isFiltering?: boolean;
    children?: React.ReactNode;
    extra?: React.ReactNode;
    selectedCount?: number;
    advancedSearch?: ToolbarAdvancedSearchConfig;
  }>;
} = ({
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  advancedSearch,
  actions,
  primaryAction,
  filters,
  totalCount,
  currentDataCount,
  selectedCount,
  modelName,
}) => {
  const hasSearch = searchValue !== undefined && onSearchChange !== undefined;

  return (
    <div className="page-toolbar-container">
      {(hasSearch || actions || primaryAction) && (
        <div className="pt-top-row">
          <div className="pt-search-group">
            {hasSearch && (
              <Input
                className="pt-search-input"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={onSearchChange}
                prefix={<SearchOutlined />}
                allowClear
              />
            )}
          </div>
          {(actions || primaryAction) && (
            <div className="pt-actions-column">
              <div className="pt-actions-group">
                {actions}
                {primaryAction}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="pt-bottom-row pt-filters-override">
        {filters ? (
          Array.isArray(filters) ? (
            <PageToolbar.Filters
              selectedCount={selectedCount}
              advancedSearch={advancedSearch}
            >
              {filters}
            </PageToolbar.Filters>
          ) : React.isValidElement(filters) ? (
            React.cloneElement(filters as React.ReactElement, {
              selectedCount,
              advancedSearch,
            })
          ) : (
            filters
          )
        ) : (
          <PageToolbar.Filters
            selectedCount={selectedCount}
            advancedSearch={advancedSearch}
          />
        )}
      </div>

      {currentDataCount !== undefined && totalCount !== undefined && (
        <Row style={{ marginTop: "16px", marginBottom: "8px" }}>
          <Col span={24}>
            <span style={{ color: "rgba(0, 0, 0, 0.65)" }}>
              Showing {Number(currentDataCount).toLocaleString()} of{" "}
              {Number(totalCount).toLocaleString()} {modelName || "records"}
            </span>
          </Col>
        </Row>
      )}

      {advancedSearch?.activeFields && advancedSearch.activeFields.length > 0 && (
        <div className="pt-active-fields-row">
          <span className="pt-active-fields-label">Active filters:</span>
          <div className="pt-active-fields-list">
            {advancedSearch.activeFields.map((field, index) => {
              const renderOperator = (op: string) => {
                switch (op) {
                  case "=":
                    return "=";
                  case "<>":
                    return "!=";
                  case "ILIKE":
                    return "contains";
                  case "BETWEEN":
                    return "between";
                  case "OR":
                    return "or";
                  case ">":
                    return ">";
                  case "<":
                    return "<";
                  default:
                    return ":";
                }
              };

              const isBetween =
                field.operator === "BETWEEN" || field.hasBetween;
              const filterText = isBetween
                ? `${field.value} and ${field.betweenValue}`
                : field.value?.toString() || "—";

              return (
                <span key={index} className="pt-active-field-chip">
                  <span className="pt-active-field-name">
                    {formatSnakeCase(field.name)}
                  </span>
                  <span className="pt-active-field-sep">
                    {renderOperator(field.operator)}
                  </span>
                  <span className="pt-active-field-value">{filterText}</span>
                  {advancedSearch.onFieldRemove && (
                    <button
                      className="pt-active-field-remove"
                      onClick={() => advancedSearch.onFieldRemove!(field)}
                      title={`Remove ${field.name} filter`}
                    >
                      <CloseOutlined />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const getIconForFilter = (label: string): React.ReactNode => {
  const normalized = label.toLowerCase();

  const iconMappings = [
    { keywords: ["status", "state"], icon: <CheckCircleOutlined /> },
    {
      keywords: [
        "assigned",
        "user",
        "requested",
        "exported",
        "author",
        "generated",
      ],
      icon: <UserOutlined />,
    },
    {
      keywords: ["provider", "bank", "company", "customer", "source"],
      icon: <GlobalOutlined />,
    },
    {
      keywords: ["category", "product", "department", "scheme", "plan"],
      icon: <AppstoreOutlined />,
    },
    {
      keywords: ["type", "channel", "format", "file", "template"],
      icon: <FileTextOutlined />,
    },
    {
      keywords: ["date", "time", "period", "quarter", "year", "month"],
      icon: <CalendarOutlined />,
    },
    {
      keywords: [
        "id",
        "reference",
        "ref",
        "number",
        "code",
        "amount",
        "external",
      ],
      icon: <BarcodeOutlined />,
    },
    {
      keywords: ["action", "control", "view", "interaction"],
      icon: <ControlOutlined />,
    },
    {
      keywords: ["history", "audit", "log", "context", "change"],
      icon: <HistoryOutlined />,
    },
    { keywords: ["data", "model", "record"], icon: <DatabaseOutlined /> },
  ];

  const match = iconMappings.find((m) =>
    m.keywords.some((k) => normalized.includes(k)),
  );
  return match?.icon || <FilterOutlined />; // Default fallback for consistent padding
};

PageToolbar.Actions = ({ children }) => (
  <div className="pt-actions-group">{children}</div>
);

PageToolbar.Filters = ({
  configs,
  sort,
  onClear,
  isFiltering,
  children,
  extra,
  selectedCount,
  advancedSearch,
}) => (
  <div className="pt-filters-row">
    <div className="pt-filters-left">
      {advancedSearch && (
        <Button className="btn-secondary" onClick={advancedSearch.onOpen}>
          <ControlOutlined /> Advanced Search
        </Button>
      )}
      {configs?.map((config) => (
        <FilterDropdown
          key={config.label}
          label={config.label}
          options={config.options}
          selectedValues={config.selectedValues}
          onChange={config.onChange}
          onSearch={config.onSearch}
          loading={config.loading}
          placeholder={config.placeholder || config.label}
          width={config.width}
          prefixIcon={config.icon || getIconForFilter(config.label)}
          mode={config.mode}
        />
      ))}

      {children}

      {sort && <SortGroup sort={sort} />}
    </div>

    <div className="pt-filters-right">
      {selectedCount !== undefined && selectedCount > 0 && (
        <span
          className="pt-selection-info"
          style={{ marginRight: 16, fontWeight: 500, color: "#1890ff" }}
        >
          {selectedCount} item{selectedCount !== 1 ? "s" : ""} selected
        </span>
      )}
      {isFiltering && onClear && (
        <Button type="link" className="pt-clear-filters-btn" onClick={onClear}>
          Clear all filters
        </Button>
      )}
      {extra}
    </div>
  </div>
);
